import { throttle, debounce } from 'lodash';

enum ScrollDirection {
  UP = 'up',
  DOWN = 'down',
}

const SCROLL_OFFSET = 10;

class TheViewHomeHero {
  element: HTMLElement;
  lastScrollTop: number;
  scrollOffset: number;
  scrollHandler: () => void;
  resizeHandler: () => void;
  alignmentHandler: () => void;
  linksContainer: HTMLElement | null;
  logoContainer: HTMLElement | null;
  lastMarginEnd: string = '';
  isResizing: boolean = false;
  isResized: boolean = false;
  resizeTimeout: number | null = null;
  lastKnownScrollY: number;
  isUpdatingHeight: boolean = false;
  heightUpdateInterval: number | null = null;
  heightUpdateCounter: number = 0;

  constructor(element: HTMLElement) {
    this.element = element;
    this.lastScrollTop = 0;
    this.scrollOffset = 0;
    this.scrollHandler = throttle(this.handleScroll.bind(this), 16);
    this.resizeHandler = this.handleResize.bind(this);
    this.alignmentHandler = debounce(this.adjustLinksAlignment.bind(this), 0);
    this.linksContainer = element.querySelector('.kf-o-the-view-home-hero__links');
    this.logoContainer = element.querySelector('.kf-o-the-view-home-hero__logo');
    this.isResizing = false;
    this.isResized = false;
    this.resizeTimeout = null;
    this.lastKnownScrollY = window.scrollY;
  }

  init() {
    this.setVariableHeight();
    this.adjustLinksAlignment();
    this.handleInitScrollDirection();
    this.attachEvents();
  }

  handleInitScrollDirection() {
    if (window.scrollY > 0) {
      this.element.dataset.scrollDirection = ScrollDirection.DOWN;
      this.element.dataset.isSticky = 'true';
    }
  }

  attachEvents() {
    window.addEventListener('scroll', this.scrollHandler);
    window.addEventListener('resize', this.resizeHandler);
    window.addEventListener('resize', this.alignmentHandler);
  }

  detachEvents() {
    window.removeEventListener('scroll', this.scrollHandler);
    window.removeEventListener('resize', this.resizeHandler);
    window.removeEventListener('resize', this.alignmentHandler);
  }

  setVariableHeight() {
    this.isUpdatingHeight = true;
    const inner = this.element.querySelector<HTMLElement>('.kf-o-the-view-home-hero__inner');
    const rect = inner?.getBoundingClientRect();

    if (rect) {
      this.element.style.setProperty('--height', `${rect.height}px`);
    }
    requestAnimationFrame(() => {
      this.isUpdatingHeight = false;
    });
  }

  updateHeightRepeated(maxIterations: number) {
    this.setVariableHeight();
    this.heightUpdateCounter++;
    if (this.heightUpdateCounter >= maxIterations) {
      this.stopHeightUpdateInterval();
    }
  }

  startHeightUpdateInterval(maxIterations: number = 30) {
    if (this.heightUpdateInterval) {
      clearInterval(this.heightUpdateInterval);
    }
    this.heightUpdateCounter = 0;
    if (this.isResized) {
      this.heightUpdateInterval = window.setInterval(() => {
        this.updateHeightRepeated(maxIterations);
      }, 16);
      this.isResized = false;
    }
  }

  stopHeightUpdateInterval() {
    if (this.heightUpdateInterval) {
      clearInterval(this.heightUpdateInterval);
      this.heightUpdateInterval = null;
    }
  }

  handleResize() {
    const rect = this.element.getBoundingClientRect();

    if (rect.top > 0) this.setVariableHeight();

    this.isResizing = true;
    this.isResized = true;
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    this.resizeTimeout = window.setTimeout(() => {
      this.isResizing = false;
      this.resizeTimeout = null;
    }, 200);
    if (rect.top > 0) this.startHeightUpdateInterval();
  }

  setResizeTimeout(timeoutMs: number, rect?: DOMRect) {
    this.isResizing = true;
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    this.resizeTimeout = window.setTimeout(() => {
      this.isResizing = false;
      this.resizeTimeout = null;
      if (rect && rect.top > 0) this.setVariableHeight();
    }, timeoutMs);
  }

  handleScroll() {
    const currentScrollY = window.scrollY;

    if (this.isResizing || this.isUpdatingHeight) {
      return;
    }
    const scrollDifference = Math.abs(currentScrollY - this.lastKnownScrollY);

    if (scrollDifference < SCROLL_OFFSET) {
      return;
    }
    const rect = this.element.getBoundingClientRect();

    this.lastKnownScrollY = currentScrollY;
    if (rect.top > 0) {
      this.element.removeAttribute('data-scroll-direction');
      this.element.removeAttribute('data-is-sticky');
      // Start continuous height updates when scrolled to top (animation active)
      if (currentScrollY < 10) this.startHeightUpdateInterval();

      return;
    }

    // Stop height updates when not at top
    this.stopHeightUpdateInterval();

    const { direction, isScrollOffsetUpdated } = this.getScrollDirection();

    this.element.dataset.isSticky = 'true';
    if (isScrollOffsetUpdated) {
      this.element.dataset.scrollDirection = direction;
    }
  }

  getScrollDirection() {
    const scrollTop = window.scrollY;
    let direction = ScrollDirection.UP;

    if (scrollTop > this.lastScrollTop) {
      direction = ScrollDirection.DOWN;
    }
    this.scrollOffset = Math.abs(this.lastScrollTop - scrollTop);
    const isScrollOffsetUpdated = this.scrollOffset >= SCROLL_OFFSET;

    if (isScrollOffsetUpdated) {
      this.lastScrollTop = scrollTop;
    }

    return {
      direction,
      isScrollOffsetUpdated,
    };
  }

  adjustLinksAlignment() {
    if (!this.linksContainer || !this.logoContainer) return;

    // Tablet and Desktop always center and last link margin end is 0
    if (window.innerWidth >= 768) {
      if (this.linksContainer.style.getPropertyValue('--links-justify-content') !== 'center') {
        this.linksContainer.style.setProperty('--links-justify-content', 'center');
      }
      if (this.linksContainer.style.getPropertyValue('--links-margin-end') !== '0') {
        this.linksContainer.style.setProperty('--links-margin-end', '0');
      }
      if (this.lastMarginEnd !== '0') {
        this.linksContainer.style.setProperty('--links-last-margin', '0');
        this.lastMarginEnd = '0';
      }

      return;
    }

    // Mobile - adjust based on logo and links width
    const logoWidth = this.logoContainer.offsetWidth;
    const links = this.linksContainer.querySelectorAll('.kf-a-link');
    let totalLinksWidth = 0;

    links.forEach((link) => {
      totalLinksWidth += (link as HTMLElement).offsetWidth;
    });

    const shouldCenter = totalLinksWidth <= logoWidth;

    this.linksContainer.style.setProperty('--links-justify-content', shouldCenter ? 'center' : 'flex-start');
    this.linksContainer.style.setProperty('--links-margin-end', shouldCenter ? '32px' : '0');
    this.linksContainer.style.setProperty('--links-last-margin', shouldCenter ? '0' : '32px');
    this.lastMarginEnd = shouldCenter ? '0' : '32px';
  }

  destroy() {
    this.stopHeightUpdateInterval();
    this.detachEvents();
  }
}

export default TheViewHomeHero;
